import React from 'react';
import { Spin, Alert } from 'antd';
import { useSelector } from 'react-redux';

const AnnotationIframe = ({ annotationUrl, onLoad, onError, style = {} }) => {
    const { token, companyId } = useSelector(state => state.auth);
    const { meetingCommonInfo } = useSelector(state => state.meeting);
    const [isLoading, setIsLoading] = React.useState(true);
    const [hasError, setHasError] = React.useState(false);
    const iframeRef = React.useRef(null);

    // Add authentication token and meeting data to annotation URL
    const iframeUrl = React.useMemo(() => {
        if (!annotationUrl) return null;
        
        const url = new URL(annotationUrl);
        url.searchParams.set('token', token);
        url.searchParams.set('companyId', companyId);
        
        // Add meeting metadata if available
        if (meetingCommonInfo?.meetingId) {
            url.searchParams.set('meetingId', meetingCommonInfo.meetingId);
        }
        if (meetingCommonInfo?.meetingDate) {
            url.searchParams.set('meetingDate', meetingCommonInfo.pdfMeetingDate);
        }
        if (meetingCommonInfo?.originalDate) {
            // URL.searchParams.set() handles encoding automatically - no manual encoding needed
            const dateString = new Date(meetingCommonInfo.pdfOriginalMeetingDate).toUTCString();
            url.searchParams.set('originalDate', dateString);
        }
        
        return url.toString();
    }, [annotationUrl, token, companyId, meetingCommonInfo]);

    const handleIframeLoad = () => {
        setIsLoading(false);
        if (onLoad) onLoad();
    };

    const handleIframeError = () => {
        setIsLoading(false);
        setHasError(true);
        if (onError) onError();
    };

    if (!iframeUrl) {
        return (
            <Alert
                message="Invalid Annotation URL"
                description="The annotation URL is not available."
                type="error"
                showIcon
            />
        );
    }

    return (
        <div style={{ 
            position: 'relative', 
            width: '100%', 
            height: '100%', 
            minHeight: style.minHeight || '100%',
            display: 'flex',
            flexDirection: 'column',
            ...style 
        }}>
            {isLoading && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 1
                }}>
                    <Spin size="large" />
                </div>
            )}
            
            {hasError && (
                <Alert
                    message="Failed to Load Annotation"
                    description="The annotation system could not be loaded. Please try again."
                    type="error"
                    showIcon
                    style={{ margin: 16 }}
                />
            )}

            <iframe
                ref={iframeRef}
                src={iframeUrl}
                style={{
                    width: '100%',
                    height: '100%',
                    minHeight: style.minHeight || '100%',
                    border: 'none',
                    borderRadius: '8px',
                    display: hasError ? 'none' : 'block',
                    flex: 1
                }}
                allow="microphone; camera; fullscreen"
                title="PDF Annotation"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
            />
        </div>
    );
};

export default AnnotationIframe; 